import { FileCollectionItemT, FileCollectionT, ProjectClientT } from 'bovaljaren-models';
import panzoom from 'panzoom';
import React, { ReactElement, useContext, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ProjectApi } from '../api';
import FileCollectionApi from '../api/api-definitions/file-collection/file-collection-api';
import { ErrorT } from '../api/api-definitions/helper-api';
import { getPolygonMeasurements } from '../components/image/mapping/mapping.helper';
import { isMobile } from '../utils/app.helper';
import { uuidv4 } from '../uuid';
import { AppContext } from './app.state';

export function useQuery() {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const contentId = 'content';

const AppInit: React.FC = ({ children }): ReactElement => {
    const query = useQuery();
    const { pId } = useParams<{ pId: string }>();
    const projectId = (pId && parseInt(pId)) || undefined;

    const { state, setLoading, filterState, presentationState, favoriteState } = useContext(AppContext);

    useEffect(() => {
        window.addEventListener('resize', () => {
            if (!state?.pymContextFrame) return;

            const currentHeight = document.getElementById('bv-root-element')?.offsetHeight;
            state?.pymContextFrame?.sendHeight && currentHeight && state?.pymContextFrame?.sendHeight(currentHeight);
        });
        document.addEventListener('touchstart', (event) => {
            const e = event as TouchEvent & { scale: number };

            if (e.touches.length === 2) {
                e.preventDefault();
            }
        }, { passive: false });
        document.addEventListener('gesturechange', (event) => {
            const e = event as TouchEvent & { scale: number };

            if (e.scale < 1.0) {
                e.preventDefault();
            }
            else if (e.scale > 1.0) {
                e.preventDefault();
            }
        }, { passive: false });
        document.addEventListener('fullscreenchange', (event) => {
            if (document.fullscreenElement) {
                console.log(`Element: ${document.fullscreenElement.id} entered fullscreen mode.`);
            }
            else {
                console.log('Leaving fullscreen mode.');
                presentationState?.setFullscreen(false);

                if (state?.project?.config?.forceFullscreen) {
                    presentationState?.setShowOnboarding(true);
                }
            }
        });
    });

    useEffect(() => {
        setTimeout(function sendSize () {
            if (!state?.pymContextFrame) return;

            const currentHeight = document.getElementById('bv-root-element')?.clientHeight;

            currentHeight && state?.pymContextFrame?.sendHeight && state.pymContextFrame.sendHeight(currentHeight);
        }, 200);
    });

    useEffect(() => {

        window.gtag('event', 'page_view', {
            item_list_id: pId
        });
    }, [pId]);

    useEffect(() => {

        try {
            if (!localStorage.getItem('uId'))
                localStorage.setItem('uId', uuidv4());
        }
        catch (e) {
            //
        }
    });

    useEffect(() => {
        setInterval(function sendSize () {
            if (!state?.pymContextFrame) return;

            const currentHeight = document.getElementById('bv-root-element')?.clientHeight;

            currentHeight && state?.pymContextFrame?.sendHeight && state.pymContextFrame.sendHeight(currentHeight);
        }, 100);
    });


    useEffect(() => {
        const jwtToken = query.get('jwtToken');

        if (jwtToken)
            localStorage.setItem('jwtToken', jwtToken);

        if (!projectId) {
            setLoading && setLoading('no-project-id');
        }
        projectId && ProjectApi
            .getProjectWithDataFromId(projectId)
            .catch(err => {
                setLoading && setLoading('api-error');
            })
            .then(result => {

                if (result && (result as ErrorT)?.hasError) {
                    setLoading && setLoading('api-error');
                }
                else if (result?.status === 200) {
                    return (result as Response).json() as unknown as ProjectClientT;
                }
                else if (result?.status === 500) {
                    setLoading && setLoading('api-error');
                }
                else if (result?.status === 404) {
                    setLoading && setLoading('no-project-found');
                }
                else {
                    return undefined;
                }
            })
            .then(result => {

                if (!result) {
                    return;
                }
                /**
                 * This loop combines the properties of the objectType with the objectContainer data
                 * for ease of use when presenting the corresponding data-values with their labels.
                 * as the object only contains the value of the data and no headers/types etc. which is contained in the objectType
                 */
                state?.pymContextFrame && state.pymContextFrame?.setActive && state.pymContextFrame.setActive(!!query.get('childId'));
                result.objectTypes.forEach(oT => {
                    const propertyArray = [];

                    //Creating a sparse array for the different properties of the objectType, for ease of assignment to the specific objects.
                    for (let i = 0; i < oT.properties.length; i++) {
                        propertyArray[oT.properties[i].id] = oT.properties[i];
                    }


                    for (let i = 0; i < result.objects.length; i++) {
                        if (result.objects[i].objectTypeId === oT.id) {
                            for (let j = 0; j < result.objects[i].data.length; j++) {
                                const property = propertyArray[result.objects[i].data[j].propId];

                                if (property) {
                                    const { name, sortOrder, type, baseType } = property;

                                    //combining the objectType properties with the object-data and applying it in the project-state
                                    result.objects[i].data[j] = {
                                        ...result.objects[i].data[j],
                                        sortOrder,
                                        name,
                                        type,
                                        baseType
                                    };
                                }
                            }
                        }
                    }
                });

                return result;
            })
            .then(result => {
                if (!result) {
                    return;
                }
                state?.setProject(result);

                return result;
            })
            .then((result) => {
                if (!result) {
                    return;
                }
                filterState?.configureFilters(result.objects, result.objectTypes);

                return result;
            })
            .then(result => {
                if (!result) {
                    return;
                }

                const objectId = query.get('objectId');
                const selectedObject = result.objects.find(o => o.id === objectId || o.externalSyncId === objectId);

                if (selectedObject) {
                    const viewId = result.views?.find(v => v.mappings.objects.some(mO => mO.mappingEntityId === selectedObject.id))?.id;

                    if (viewId && viewId !== result.primaryViewId) {
                        state?.setCurrentViewId(viewId);
                    }

                    if (isMobile() && !state?.selectedObjectId) presentationState?.resetPanzoomTranslate();

                    state?.setSelectedObjectId(selectedObject.id);

                    if (isMobile()) {
                        presentationState?.resetPanzoomTranslate();
                        const mapping = state?.currentView?.mappings.objects.find(mO => mO.mappingEntityId === selectedObject.id);

                        if (mapping) {
                            state?.setPreviewObjectId(selectedObject.id);
                            const measure = mapping && getPolygonMeasurements(mapping);
                            measure && setTimeout(() => {
                                presentationState?.zoomInOnPosition(measure.xMid, measure.yMid + 10);
                                presentationState?.setActivePreview(true);
                                presentationState?.setShowObjectView(true);
                                presentationState?.setShowSidebarContent(false);
                            }, 10);
                        }

                    }
                    else {
                        presentationState?.setActivePreview(true);
                        presentationState?.setShowObjectView(true);
                    }
                    !!presentationState?.activePreview && Array.from(document.getElementsByClassName('bv-mapping-fade')).forEach(o => {
                        o.classList.remove('bv-mapping-fade');
                        o.classList.add('bv-mapping-fade');
                    });
                    presentationState?.setShowOnboarding(false);
                    setLoading && setLoading('ok');
                }
            });
        projectId && FileCollectionApi.getActiveFileCollections(projectId)
            .then(result => {
                if (result?.status === 200) {
                    return (result as Response).json() as unknown as FileCollectionT[];
                }
                else {
                    return undefined;
                }
            })
            .catch(err => console.log(err))
            .then(result => {
                if (!result) return;
                state?.setFileCollections(result);
            });

    }, [state, filterState, projectId, presentationState, query, setLoading]);

    useEffect(() => {
        const contentElement = document.getElementById(contentId);

        if (contentElement) {
            const pEle = panzoom(contentElement, {
                autocenter: true,
                bounds: true,
                boundsPadding: 1,
                minZoom: 1,
                maxZoom: 2,
                onDoubleClick: function (e) {
                    return true;
                },
                onTouch: function (e) {
                    // `e` - is current touch event.

                    return false; // tells the library to not preventDefault.
                },
                beforeWheel: function (e) {
                    // allow wheel-zoom only if altKey is down. Otherwise - ignore
                    const shouldIgnore = !e.altKey;

                    return shouldIgnore;
                }
            });
            pEle.on('zoom', (e: any) => {
                const main = document.getElementById('bv-main');
                presentationState?.setZoomScale(e.getTransform().scale);

                if (main && (e.getTransform().scale > 1)) {
                    main.classList.add('bv-main--touch-action-off');
                }
                else if (main) {
                    main.classList.remove('bv-main--touch-action-off');
                }

            });
            contentElement.style.height = 'auto';
            presentationState?.setPanzoom(pEle);
        }

    }, [presentationState]);


    return (<>{children}</>);
};


export default AppInit;